import * as React from 'react';
import notify from '../../../../shared/feedback/notifications';
import * as actions from './actions/ImageProcessingActions';
import * as DownloadActions from '../../../../actions/DownloadActions';
import AutoUpdateListTrigger from '../../../../shared/AutoUpdateListTrigger';
import { connect } from 'react-redux';
import EditedImagesRow from './list/EditedImagesRow';
import { handleError } from '../../../../shared/ErrorCatching';
import { ImageType, IOrderRow } from './Entities';
import { Translate } from '../../../../shared/LanguageService';

export class EditedImagesPage extends React.Component<any, any> {
    private _updateListTrigger: AutoUpdateListTrigger;

    constructor(props: any) {
        super(props);

        this.state = {
            isLoadingListImages: false,
            isLoadingPhotoStatus: false,
            downloading: false
        };

        this.onUploadDone = this.onUploadDone.bind(this);
        this.refreshImagesAndPhotoStatus = this.refreshImagesAndPhotoStatus.bind(this);
        this.getPhotoStatus = this.getPhotoStatus.bind(this);
        this.getListImages = this.getListImages.bind(this);
        this.acceptNormalFiles = this.acceptNormalFiles.bind(this);
        this.onNormalImageUploadDone = this.onNormalImageUploadDone.bind(this);
        this.deletePhoto = this.deletePhoto.bind(this);

        this._updateListTrigger = new AutoUpdateListTrigger(this.refreshImagesAndPhotoStatus, 5000);
    }

    componentDidMount() {
        this.getListImages();
        this.props.actions
            .getCategories(this.props.orderId)
            .catch((error: any) => handleError(error));
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (nextProps && nextProps.orderId !== this.props.orderId) {
            this.getListImages(nextProps.orderId);
        } else if (nextProps && nextProps.list) {
            if (
                this.noMissingNormalOrThumbnail(nextProps.list) &&
                this.noUploadsInProgress(nextProps.list)
            ) {
                this._updateListTrigger.unsubscribe();
            } else {
                this._updateListTrigger.subscribe();
            }
        }
    }

    componentWillUnmount() {
        this._updateListTrigger.unsubscribe();
    }

    noMissingNormalOrThumbnail(list: any[]) {
        return !list.some((x) =>
            x.thumbnails.some((y) => !y.reference || (y.hasAutoScale && !y.hasUploadedNormal))
        );
    }

    noUploadsInProgress(list: any[]) {
        return !list.some((x) => x.editedPhotosUploadInProgress);
    }

    getListImages(orderId: number = this.props.orderId) {
        if (!this.state.isLoadingListImages) {
            this.setState({ isLoadingListImages: true });
            this.props.actions
                .listImages(orderId, ImageType.EDITED)
                .then(() => {
                    this.setState({ isLoadingListImages: false });
                })
                .catch((error: any) => {
                    this.setState({ isLoadingListImages: false });
                    return handleError(error);
                });
        }
    }

    getPhotoStatus() {
        if (!this.state.isLoadingPhotoStatus) {
            this.setState({ isLoadingPhotoStatus: true });
            this.props.actions
                .getPhotoStatus(this.props.orderId)
                .then(() => {
                    this.setState({ isLoadingPhotoStatus: false });
                })
                .catch((error: any) => {
                    this.setState({ isLoadingPhotoStatus: false });
                    return handleError(error);
                });
        }
    }

    refreshImagesAndPhotoStatus() {
        this.getListImages();
        this.getPhotoStatus();
    }

    onUploadDone() {
        this.refreshImagesAndPhotoStatus();
    }

    onNormalImageUploadDone() {
        this.onUploadDone();
        notify.success(Translate('image-processing/edited/normal-upload/message/done'));
    }

    deletePhoto(photoId: number): Promise<any> {
        return this.props.actions.deletePhoto(this.props.orderId, photoId);
    }

    acceptNormalFiles(
        orderRowId: number,
        file: DropzoneFile,
        done: (error?: string | Error) => void
    ) {
        let thumbnails = this.props.list.find((x: IOrderRow) => x.id === orderRowId).thumbnails;
        let fileName = thumbnails.find(
            (thumbnail) => thumbnail.filename.toLowerCase() === file.name.toLowerCase()
        );
        if (!fileName) {
            done('remove file');
            notify.failure(
                `${file.name} ${Translate('image-processing/notification/does-not-exist-on-order')}`
            );
        } else {
            done();
        }
    }

    render() {
        let additionalItems =
            this.props.photoStatus.orderRowQuantitiesInfo &&
            this.props.photoStatus.orderRowQuantitiesInfo
                .filter((x) => x.isUploadable == 0)
                .map((x) => x);

        return (
            <div>
                {this.props.list &&
                    this.props.list.map((item: IOrderRow, i: number) => (
                        <EditedImagesRow
                            key={`edited-image-row-${i}`}
                            item={item}
                            additionalItems={
                                i === 0 && additionalItems && additionalItems.length > 0
                                    ? additionalItems
                                    : undefined
                            }
                            showCustomerApproval={i === 0}
                            showModalForImage={this.props.showModalForImage}
                            editedPhotosUploadInProgress={this.props.list.some(
                                (x) => x.editedPhotosUploadInProgress
                            )}
                            numberOfPhotosInRows={this.props.numberOfPhotosInRows}
                            orderId={this.props.orderId}
                            allowStitching={this.props.allowStitching}
                            user={this.props.user}
                            photographerIsEditor={this.props.photographerIsEditor}
                            photoStatus={this.props.photoStatus}
                            categories={this.props.categories}
                            allowChangeCategories={this.props.allowChangeCategories}
                            deletePhoto={this.deletePhoto}
                            highImageSizeSettings={this.props.highImageSizeSettings}
                            normalImageSizeSettings={this.props.normalImageSizeSettings}
                            onNormalImageUploadDone={this.onNormalImageUploadDone}
                            onUploadDone={this.onUploadDone}
                            downloadFile={this.props.actions.downloadFile}
                            ctrlIsDown={this.props.ctrlIsDown}
                            showCompareImages={this.props.showCompareImages}
                            setShowCompareImages={this.props.setShowCompareImages}
                            selectedImages={this.props.selectedImages}
                            setSelectedImages={this.props.setSelectedImages}
                            setIsWriting={this.props.setIsWriting}
                        />
                    ))}
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            listImages: (orderId: number, value: number) =>
                dispatch(actions.listImages(orderId, value)),
            getPhotoStatus: (orderId: number) => dispatch(actions.getPhotoStatus(orderId)),
            getCategories: (orderId: number) => dispatch(actions.getCategories(orderId)),
            deletePhoto: (orderId: number, photoId: number) =>
                dispatch(actions.deleteEditedPhoto(orderId, photoId)),
            downloadFile: (url: string, data?: any) =>
                dispatch(DownloadActions.downloadFile(url, data))
        }
    };
}

function mapStateToProps(state: any) {
    return {
        list: state.imageProcessing.edited.list,
        user: state.user,
        numberOfPhotosInRows: state.imageProcessing.photoStatus.photosInRows,
        numberOfImages: state.imageProcessing.numberOfImages
            ? state.imageProcessing.numberOfImages.edited
            : 0,
        photoStatus: state.imageProcessing.photoStatus,
        categories: state.imageProcessing.modal.categories,
        allowChangeCategories: state.imageProcessing.modal.allowChangeCategories
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditedImagesPage);
